import React, { useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, List, ListItem, ListItemButton, ListItemText, DialogActions, Button, Typography, CircularProgress, Box, TextField } from '@mui/material';
import { useSettings } from '../../providers/settings-provider';
import routes from '../../api/routes';
import buildApiRequestConfig from '../../api/url-builder';
import useApi from '../../hooks/use-api';
import { useUser } from '../../providers/user-provider';
import { Search } from 'lucide-react';
import DeletePopup from './delete';

interface ProvisionSelectorPopupProps {
    open: boolean;
    onClose: () => void;
    // second argument is optional description for convenience
    onSelect: (provisionId: number, description?: string) => void;
}

const ProvisionSelectorPopup: React.FC<ProvisionSelectorPopupProps> = ({ open, onClose, onSelect }) => {
    const { formatDate, formatAmount } = useSettings();
    const { user } = useUser();
    const [fetchProvisions, setFetchProvisions] = useApi();
    const [provisions, setProvisions] = React.useState<Array<{ id: number; description: string; amount: number; date: string; categoryId?: number }>>([]);
    const [searchTerm, setSearchTerm] = React.useState('');
    const [showConfirmation, setShowConfirmation] = React.useState(false);
    const [selectedProvision, setSelectedProvision] = React.useState<{ id: number; description: string } | null>(null);

    // Fetch provisions when dialog opens
    useEffect(() => {
        if (open && user?.entityId) {
            const { url, method } = buildApiRequestConfig(routes.provisionTemplate.fetchByEntity, [{ param: 'entityId', value: user.entityId }]);
            setFetchProvisions({ url, method });
        }
    }, [open, user?.entityId]);

    // Handle fetched provisions
    useEffect(() => {
        const { data, isLoading, error } = fetchProvisions;
        if (data && !isLoading && Array.isArray(data)) {
            setProvisions(data.map((prov: any) => ({
                id: prov.id,
                description: prov.description,
                amount: prov.amount,
                date: prov.startDate,
                categoryId: prov.categoryId
            })));
        }
    }, [fetchProvisions]);

    const handleSelect = (provisionId: number) => {
        const selected = provisions.find(p => p.id === provisionId);
        if (selected) {
            setSelectedProvision({ id: selected.id, description: selected.description });
            setShowConfirmation(true);
        }
    };

    const handleConfirmSelection = () => {
        if (selectedProvision) {
            onSelect(selectedProvision.id, selectedProvision.description);
            setShowConfirmation(false);
            setSelectedProvision(null);
            setSearchTerm('');
            onClose();
        }
    };

    const handleClose = () => {
        setSearchTerm(''); // Clear search when closing without selecting
        onClose();
    };

    return (
        <>
            <DeletePopup
                open={showConfirmation}
                message="Confirm Selection"
                description={`Relate selected transactions to <strong>${selectedProvision?.description}</strong>?`}
                confirmLabel="Confirm"
                cancelLabel="Cancel"
                onConfirm={handleConfirmSelection}
                onCancel={() => {
                    setShowConfirmation(false);
                    setSelectedProvision(null);
                }}
            />
            <Dialog 
                open={open} 
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
            >
            <DialogTitle fontWeight={600}>Select Related Provision</DialogTitle>
            <DialogContent>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search by description or amount..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ mb: 1 }}
                    InputProps={{
                        startAdornment: <span style={{ marginRight: 8 }}><Search size={18} color="var(--color-primary)" /></span>
                    }}
                />
                {fetchProvisions.isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : provisions.length === 0 ? (
                    <Typography color="textSecondary" sx={{ py: 2, textAlign: 'center' }}>
                        No provisions available. Create a provision first.
                    </Typography>
                ) : (
                    <List sx={{ maxHeight: 250, overflowY: 'auto' }}>
                        {provisions
                            .filter((provision) => {
                                if (!searchTerm) return true;
                                const searchLower = searchTerm.toLowerCase();
                                const descriptionMatch = provision.description.toLowerCase().includes(searchLower);
                                const amountMatch = provision.amount.toString().includes(searchTerm);
                                return descriptionMatch || amountMatch;
                            })
                            .map((provision, idx) => (
                            <ListItem key={provision.id} disablePadding sx={{ background: idx % 2 === 0 ? 'rgba(0, 0, 0, 0.04)' : 'transparent', '&:hover': { background: 'rgba(55, 255, 198, 0.32)' } }}>
                                <ListItemButton onClick={() => handleSelect(provision.id)}>
                                    <ListItemText
                                        primary={provision.description}
                                        secondary={`${formatAmount?.(provision.amount)} • ${formatDate(new Date(provision.date))}`}
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
            </DialogActions>
        </Dialog>
        </>
    );
};

export default ProvisionSelectorPopup;
